import { useTranslation } from "next-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";

import { useSubscription } from "@/hooks/useSubscription";
import { paymentApi } from "@/redux/api/paymentApi";
import { useNotification } from "@/hooks/useNotification";
import { useSubscriptionModal } from "@/hooks/useSubscriptionModal";

import { IconPurchaseAi, IconPurchaseScheduleCalendar } from "../Icons";

import { PurchasePopup } from "./PurchasePopup";

export const GenerativeCreditsPurchasePopup = ({
  isOpened = false,
  onClose,
}: {
  isOpened?: boolean;
  onClose?: () => void;
}) => {
  const { t } = useTranslation("common");
  const { contextHolder, notify } = useNotification();

  const { openPlanSwitchModal } = useSubscriptionModal();

  /**
   * 마운트 여부 스테이트
   *
   * 팝업이 열리고 잠시 후에 마운트 여부를 true로 취급
   */
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    if (isOpened) {
      setIsMounted(true);
    } else {
      setIsMounted(false);
    }

    return () => {
      setIsMounted(false);
    };
  }, [isOpened]);

  const { data: plans } = paymentApi.useGetPlansQuery({});

  const {
    plan,
    subscriptionInfo,
    refetch: refetchSubscription,
  } = useSubscription();

  const isPaid = (plan?.level.current ?? 0) > 0;
  const isUnlimited = !!plan?.unlimited;
  const isEnterprise = plan?.normalized === "enterprise";

  /**
   * 현재 구독 상태 등 정리
   */
  const currentPlanFullName = useMemo(() => {
    return `${plan?.raw} (${
      plan?.interval === "month"
        ? "Monthly"
        : plan?.interval === "year"
        ? "Annual"
        : ""
    })`;
  }, [plan]);
  const currentBillingCycle = useMemo(() => {
    if (!plan?.servicePeriodStart || !plan?.servicePeriodEnd) {
      return;
    }

    return {
      start: plan.servicePeriodStart,
      end: plan.servicePeriodEnd,
    };
  }, [plan]);
  const nextBillingDate = plan?.servicePeriodEnd ?? undefined;

  /**
   * 현재 구매한 애드온 크레딧
   */
  const subscriptionId = subscriptionInfo?.subscriptionId;
  const currentAddOn = useMemo(() => {
    const matchingPlanName = plan?.normalized;

    if (!matchingPlanName || !subscriptionInfo) {
      return;
    }

    return subscriptionInfo.items?.find((item) => {
      const lowerCasedPlanName = item.planName.toLocaleLowerCase();

      return (
        lowerCasedPlanName.includes("generative") &&
        lowerCasedPlanName.includes(matchingPlanName)
      );
    });
  }, [subscriptionInfo, plan]);
  const subscriptionItemId = currentAddOn?.subscriptionItemId;
  const currentAddOnQuantity = currentAddOn?.quantity ?? 0;

  /**
   * 취소, 다운그레이드 등 예약된 건이 있는지 확인
   */
  const isScheduledDowngrade = useMemo(() => {
    const schedule = subscriptionInfo?.schedule;
    if (!schedule) {
      return false;
    }

    const currentScheduleItems = schedule.current;
    const nextScheduleItems = schedule.next;

    if (!currentScheduleItems || !Array.isArray(currentScheduleItems)) {
      return false;
    }

    if (!nextScheduleItems || !Array.isArray(nextScheduleItems)) {
      return false;
    }

    for (const currentScheduleItem of currentScheduleItems) {
      const nextScheduleItem = nextScheduleItems.find((item) => {
        return item.price === currentScheduleItem.price;
      });

      const currentScheduleItemQuantity = currentScheduleItem.quantity;
      const nextScheduleItemQuantity = nextScheduleItem?.quantity ?? 0;

      if (currentScheduleItemQuantity > nextScheduleItemQuantity) {
        return true;
      }
    }

    return false;
  }, [subscriptionInfo?.schedule]);

  /**
   * 현재 사용자의 플랜에 따른 구매 가능한 애드온 플랜 찾기
   */
  const addOnPlan = useMemo(() => {
    const matchingPlanName =
      plan?.level.current === 1
        ? "personal"
        : plan?.level.current === 2
        ? "team"
        : undefined;

    if (!matchingPlanName) {
      return;
    }

    return plans?.find((p) => {
      const lowerCasedPlanName = p.planName.toLocaleLowerCase();

      return (
        p.mark === "addOn" && lowerCasedPlanName.includes(matchingPlanName)
      );
    });
  }, [plans, plan]);

  const addOnPlanName = addOnPlan?.planName;
  const addOnPlanConstraints =
    plan?.interval === "month"
      ? addOnPlan?.constraints.monthly
      : plan?.interval === "year"
      ? addOnPlan?.constraints.yearly
      : undefined;
  const addOnPlanPricePerMonth =
    plan?.interval === "month"
      ? addOnPlan?.pricePerMonth
      : plan?.interval === "year"
      ? addOnPlan?.pricePerYear
      : undefined;
  const addOnPlanPrice =
    plan?.interval === "month"
      ? addOnPlan?.pricePerMonth
      : plan?.interval === "year"
      ? addOnPlan?.pricePerYear
      : undefined;
  const addOnPlanPriceId =
    plan?.interval === "month"
      ? addOnPlan?.planId.monthly
      : plan?.interval === "year"
      ? addOnPlan?.planId.yearly
      : undefined;
  const addOnPlanCredits = addOnPlanConstraints?.genCredit;

  const initialAddOnQuantity = currentAddOnQuantity;
  const initialExtraQuantity = 1;

  /**
   * TODO: 엑스트라 크레딧 기능이 추가되면 그에 대한 대응 필요함
   */
  // const [selectedProductIndex, setSelectedProductIndex] = useState(0);
  const [, setSelectedProductIndex] = useState(0);
  const [addOnQuantity, setAddOnQuantity] = useState(
    () => initialAddOnQuantity || 1,
  );
  /**
   * TODO: 엑스트라 크레딧 기능이 추가되면 그에 대한 대응 필요함
   */
  // const [extraQuantity, setExtraQuantity] = useState(initialExtraQuantity);
  const [, setExtraQuantity] = useState(initialExtraQuantity);

  useEffect(() => {
    setSelectedProductIndex(0);
    setAddOnQuantity(initialAddOnQuantity || 1);
    setExtraQuantity(initialExtraQuantity);
  }, [isOpened, initialAddOnQuantity, initialExtraQuantity]);

  const editingProduct = useMemo(() => {
    return [
      {
        productTitle: `+${addOnPlanCredits} ${addOnPlanName}`,
        productPrice: `+$${addOnPlanPricePerMonth}`,
        isMonthly: true,
        isBilledAnnually: plan?.interval === "year",
        unit: t("Unit"),
        min: 1,
        max: 10,
        descriptions: [
          <>
            <IconPurchaseScheduleCalendar />{" "}
            {t("Auto-renewed with your subscription")}
          </>,
        ],
        isVisibleQuantity: true,
        quantity: addOnQuantity,
        isSelected: true,
      },
      /**
       * TODO: 추후 추가 예정
       */
      // {
      //   title: t("One-Time Extra Credits"),
      //   productTitle: t("{{quantity}} Generative Credits", {
      //     quantity: 100,
      //   }),
      //   productPrice: "$10",
      //   unit: t("Pack"),
      //   min: 1,
      //   max: 10,
      //   descriptions: [
      //     <>
      //       <IconPurchaseScheduleCalendar />{" "}
      //       {t("Expires {{days}} days after purchase", { days: 30 })}
      //     </>,
      //   ],
      // },
    ];
  }, [
    addOnPlanCredits,
    addOnPlanName,
    addOnPlanPricePerMonth,
    plan?.interval,
    t,
    addOnQuantity,
  ]);

  /**
   * 오늘 결제 금액 계산 관련
   */
  // debounce 해서 숫자가 바뀌면 바로 조회하지 않고 잠시 후에 조회하도록 함
  const [debouncedAddOnQuantity, setDebouncedAddOnQuantity] = useState(
    () => addOnQuantity || 1,
  );

  useEffect(() => {
    setDebouncedAddOnQuantity(initialAddOnQuantity || 1);
  }, [isOpened, initialAddOnQuantity]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (addOnQuantity % 1 !== 0) {
        return;
      }

      setDebouncedAddOnQuantity(addOnQuantity);
    }, 1000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [addOnQuantity]);

  const isQuantityChanged = debouncedAddOnQuantity !== addOnQuantity;

  const {
    data: fetchedInvoicePreviewData,
    isFetching: isInvoicePreviewFetching,
    isError: isInvoicePreviewError,
  } = paymentApi.useGetInvoicePreviewQuery(
    {
      type: "addon",
      subscriptionId: subscriptionId ?? "",
      items: [
        {
          id: subscriptionItemId,
          price: addOnPlanPriceId ?? "",
          quantity: debouncedAddOnQuantity,
        },
      ],
    },
    {
      skip:
        !isMounted ||
        !subscriptionId ||
        !addOnPlanPriceId ||
        !debouncedAddOnQuantity ||
        (isPaid && !isUnlimited),
    },
  );

  const invoicePreviewData = useMemo(() => {
    /**
     * 구 요금제 사용자인 경우 가상으로 만들어서 반환
     */
    if (isPaid && !isUnlimited && !isEnterprise) {
      return {
        due_today: (addOnPlanPricePerMonth ?? 0) * addOnQuantity,
        items: [
          {
            planName: addOnPlanName ?? "",
            description: ` - ${addOnPlanCredits} credits`,
            name: "Generative Credits",
            quantity: addOnQuantity,
            unit_amount: addOnPlanPricePerMonth,
            amount: (addOnPlanPricePerMonth ?? 0) * addOnQuantity,
            price: {
              recurring: {
                interval: plan?.interval,
              },
              lookup_key: "genCredit",
            },
          },
        ],
      };
    }

    return fetchedInvoicePreviewData;
  }, [
    fetchedInvoicePreviewData,
    isPaid,
    isEnterprise,
    isUnlimited,
    addOnPlanName,
    addOnPlanCredits,
    addOnPlanPricePerMonth,
    addOnQuantity,
    plan,
  ]);

  /**
   * 계산된 가격 정리
   */
  const todayBillingTotalPrice = invoicePreviewData?.due_today ?? 0;

  let discountInfo = null;
  const discount = subscriptionInfo?.discount;
  if (discount) {
    if (discount.interval === "yearly") {
      discountInfo = {
        title: "10% off",
      };
    } else {
      discountInfo = {
        title: "50% off",
      };
    }
  }

  const calculatedAddOnPrice = (addOnPlanPrice ?? 0) * addOnQuantity;

  const totalPrice = calculatedAddOnPrice;

  const { details, prorationDetails } = useMemo(() => {
    if (!invoicePreviewData) {
      return {};
    }

    /**
     * TODO: 지금은 엑스트라가 없고 애드온만 있어서 애드온만 비교했음\
     * TODO: 추후 엑스트라가 추가되면 엑스트라도 비교해야 함
     */
    if (initialAddOnQuantity >= debouncedAddOnQuantity) {
      return {
        details: [
          {
            title: addOnPlanName,
            description: `(${addOnPlanCredits} credits) x ${addOnQuantity} ${t(
              "Unit",
            )}`,
            price: calculatedAddOnPrice,
            noDiscount: true,
          },
        ],
      };
    }

    const items = invoicePreviewData.items ?? [];

    const mergingDetails = [];
    const mergingProrationDetails = [];

    const getDescription = (item: (typeof items)[number]) => {
      const pName = item.planName;
      const pInterval = item.price.recurring.interval;
      const pQuantity = item.quantity;

      if (!item.price.lookup_key) return "";
      if (item.price.lookup_key.includes("base")) {
        return `(${pInterval === "month" ? "Monthly" : "Annual"})${
          pName.toLowerCase().includes("team")
            ? ` x ${pQuantity} ${t("Seat")}`
            : ""
        }`;
      } else if (item.price.lookup_key.includes("avatar")) {
        return `(${
          pInterval === "month" ? "Monthly" : "Annual"
        }) x ${pQuantity} ${t("Qty")}`;
      } else if (item.price.lookup_key.includes("genCredit")) {
        const credit = Number(
          item.description?.split(" - ")?.[1]?.substring(0, 3) ?? "0",
        );
        const month = pInterval === "month" ? 1 : 12;
        return `(${credit * month} credits) x ${pQuantity} ${t("Unit")}`;
      }
    };

    const getPriority = (item: (typeof items)[number]) => {
      if (!item.price.lookup_key) {
        return 999999;
      }

      if (item.price.lookup_key.includes("base")) {
        return 0;
      }

      if (item.price.lookup_key.includes("avatar")) {
        return 11;
      }

      if (item.price.lookup_key.includes("genCredit")) {
        return 12;
      }

      return 999999;
    };

    const sortedItems = [...items].sort((a, b) => {
      const aPriority = getPriority(a);
      const bPriority = getPriority(b);

      return aPriority - bPriority;
    });

    for (const item of sortedItems) {
      if (item.amount > 0) {
        mergingDetails.push({
          title: item.planName,
          description: getDescription(item),
          price: item.amount,
        });
      } else {
        mergingProrationDetails.push({
          title: item.planName,
          description: getDescription(item),
          price: item.amount,
        });
      }
    }

    return {
      details: mergingDetails,
      prorationDetails: mergingProrationDetails,
    };
  }, [
    invoicePreviewData,
    t,
    initialAddOnQuantity,
    debouncedAddOnQuantity,
    addOnPlanName,
    addOnPlanCredits,
    addOnQuantity,
    calculatedAddOnPrice,
  ]);

  const nextBillingTotalPrice = totalPrice;

  /**
   * 결제 등 진행
   */
  const [changeAddOn, { isLoading: isLoadingChangeAddOn }] =
    paymentApi.useChangeAddOnMutation();

  const handleConfirm = useCallback(async () => {
    if (!subscriptionId || !addOnPlanPriceId || !debouncedAddOnQuantity) {
      return;
    }

    if (isQuantityChanged) {
      return;
    }

    /**
     * 비로그인, 엔터프라이즈, 무료 플랜은 구매 불가
     *
     * 애초에 이 팝업이 표시되지 않음
     */
    if (plan?.level.current === 0 || plan?.level.current === 3) {
      return;
    }

    /**
     * 무제한 플랜이 아닌 경우 플랜 변경 팝업 표시
     */
    if (!plan?.unlimited) {
      openPlanSwitchModal();

      onClose?.();

      return;
    }

    try {
      await changeAddOn({
        subscriptionId,
        items: [
          {
            id: subscriptionItemId,
            price: addOnPlanPriceId ?? "",
            quantity: debouncedAddOnQuantity,
          },
        ],
      }).unwrap();

      await refetchSubscription();

      onClose?.();

      notify.success(
        t("Your Add-On has been updated and the changes are now in effect."),
      );
    } catch (e) {
      // TODO: 에러 종류에 따라 다른 메시지 추가 필요
      if (false) {
        // 사용자 결제 정보 오류로 인한 결제 실패인 경우
        notify.warning(
          t(
            "Add-On update failed. Please check your billing info and try again.",
          ),
        );
      } else {
        // 그 외 일반적인 오류
        notify.warning(
          t(
            "Add-On update failed. Please try again. If the issue persists, contact support.",
          ),
        );
      }
    }
  }, [
    subscriptionId,
    addOnPlanPriceId,
    debouncedAddOnQuantity,
    isQuantityChanged,
    plan,
    changeAddOn,
    onClose,
    subscriptionItemId,
    refetchSubscription,
    notify,
    t,
    openPlanSwitchModal,
  ]);

  /**
   * 결제 포탈 이동
   */
  const router = useRouter();

  const [successUrl, setSuccessUrl] = useState("");

  useEffect(() => {
    setSuccessUrl(window.location.href);
  }, [router]);

  const { data: billingPortalUrl } = paymentApi.useGetBillingPortalUrlQuery(
    {
      successUrl,
    },
    {
      skip: !isMounted || !successUrl,
    },
  );

  const goToBillingPortal = useCallback(async () => {
    if (!billingPortalUrl) {
      return;
    }

    window.open(billingPortalUrl, "_blank");
  }, [billingPortalUrl]);

  return (
    <>
      <PurchasePopup
        isOpened={isOpened}
        onClose={() => {
          if (isLoadingChangeAddOn) {
            return;
          }

          onClose?.();
        }}
        title={t("Add More Generative Credits")}
        productsTitle={t("Generation Credits")}
        productTitleGradient
        productTitleIcon={<IconPurchaseAi />}
        products={editingProduct}
        onChangeSelectedProduct={(index) => {
          setSelectedProductIndex(index);
        }}
        onChangeProductQuantity={(index, quantity) => {
          if (index === 0) {
            setAddOnQuantity(quantity);
          } else {
            // setOneTimeQuantity(quantity);
          }
        }}
        productsDescriptions={[
          t(
            "Add-ons follow your subscription and will be canceled if the plan ends.",
          ),
          t("All purchases are final and non-refundable."),
        ]}
        currentPlan={currentPlanFullName}
        currentBillingCycle={currentBillingCycle}
        totalPrice={totalPrice}
        details={details}
        prorationDetails={prorationDetails}
        todayPrice={todayBillingTotalPrice}
        loadingTodayPrice={isInvoicePreviewFetching || isQuantityChanged}
        nextBillingPrice={nextBillingTotalPrice}
        nextBillingDate={nextBillingDate}
        confirmText={t("Confirm Selection")}
        billingCycleCautionTitle={
          isScheduledDowngrade ? t("📌 Upgrade notice") : undefined
        }
        billingCycleCautionDescription={
          isScheduledDowngrade
            ? t(
                "Any scheduled cancellation or downgrade will be automatically removed when you upgrade your plan. Your new plan will take effect immediately, and you'll be charged today.",
              )
            : undefined
        }
        disabledConfirm={isInvoicePreviewError || isQuantityChanged}
        loadingConfirm={isLoadingChangeAddOn}
        onConfirm={handleConfirm}
        goToBillingPortal={goToBillingPortal}
        discount={discountInfo}
      />

      {contextHolder}
    </>
  );
};
